import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface ThemeState {
  isDarkMode: boolean;
  toggleTheme: () => void;
  setTheme: (isDark: boolean) => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => {
      // Initialize theme from localStorage or default to light (matching landing page)
      const initializeTheme = () => {
        if (typeof window === 'undefined') return false;
        
        // Check localStorage for saved theme preference
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme === 'dark') return true;
        if (savedTheme === 'light') return false;
        
        // Default to light theme (matching landing page)
        return false;
      };

      const initialDarkMode = initializeTheme();

      // Sync with HTML data-theme attribute
      if (typeof window !== 'undefined') {
        const html = document.documentElement;
        html.setAttribute('data-theme', initialDarkMode ? 'dark' : 'light');
      }

      return {
        isDarkMode: initialDarkMode,
        toggleTheme: () => {
          const newDarkMode = !get().isDarkMode;
          set({ isDarkMode: newDarkMode });
          
          // Sync with HTML data-theme attribute
          if (typeof window !== 'undefined') {
            const html = document.documentElement;
            html.setAttribute('data-theme', newDarkMode ? 'dark' : 'light');
            localStorage.setItem('theme', newDarkMode ? 'dark' : 'light');
          }
        },
        setTheme: (isDark: boolean) => {
          set({ isDarkMode: isDark });
          
          // Sync with HTML data-theme attribute
          if (typeof window !== 'undefined') {
            const html = document.documentElement;
            html.setAttribute('data-theme', isDark ? 'dark' : 'light');
            localStorage.setItem('theme', isDark ? 'dark' : 'light');
          }
        },
      };
    },
    {
      name: 'theme-storage',
      // Custom storage to sync with localStorage 'theme' key (matching landing page)
      storage: {
        getItem: (name: string) => {
          const value = localStorage.getItem('theme');
          if (!value) return null;
          return JSON.stringify({ state: { isDarkMode: value === 'dark' } }) as any;
        },
        setItem: (name: string, value: any) => {
          const parsed = JSON.parse(value);
          const theme = parsed.state.isDarkMode ? 'dark' : 'light';
          localStorage.setItem('theme', theme);
        },
        removeItem: (name: string) => {
          localStorage.removeItem('theme');
        },
      } as any,
    }
  )
);
